import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  EyeOutlined,
  FileAddOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  TruckOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import API_CONFIG from "../config/api.js";
import { useSelection } from "../SelectionContext";
import { useAuth } from "../context/AuthContext";
import RowActionsMenu from "../components/RowActionsMenu";
import {
  createSalesDelivery,
  downloadSalesDeliveryPdf,
  fetchEligibleSalesForDelivery,
  fetchSalesDeliveries,
  fetchSalesDeliveryDetail,
  type EligibleSaleForDelivery,
  type SalesDelivery,
  type SalesDeliveryDetail,
} from "./deliveryApi";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface ClientOption {
  id: number;
  name?: string;
  company_name?: string;
  Nom_Raison_Sociale?: string;
}

interface WarehouseOption {
  id: number;
  name: string;
}

interface DeliveryFiltersState {
  warehouse_id?: number;
  user_id?: number;
  invoice_number?: string;
  delivery_number?: string;
  date_range?: [Dayjs | null, Dayjs | null];
}

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const formatCurrency = (value: number | string | null | undefined) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) {
    return "0 CFA";
  }

  return `${numericValue.toLocaleString("fr-FR", {
    maximumFractionDigits: 0,
  })} CFA`;
};

const formatDate = (value: string | null | undefined, withTime = false) => {
  if (!value) {
    return "-";
  }

  return dayjs(value).format(withTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY");
};

const resolveContextId = (value: unknown) => {
  if (typeof value === "number") {
    return value;
  }

  if (value && typeof value === "object" && "id" in value) {
    const candidate = Number((value as { id?: unknown }).id);
    return Number.isFinite(candidate) && candidate > 0 ? candidate : undefined;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : undefined;
};

const getClientLabel = (client: ClientOption) =>
  client.name || client.company_name || client.Nom_Raison_Sociale || `Client ${client.id}`;

const DeliveryDetailModal: React.FC<{
  delivery: SalesDeliveryDetail | null;
  open: boolean;
  onClose: () => void;
  onPrint: (delivery: SalesDelivery) => Promise<void>;
  canPrint: boolean;
}> = ({ delivery, open, onClose, onPrint, canPrint }) => {
  const itemColumns: ColumnsType<SalesDeliveryDetail["items"][number]> = [
    {
      title: "Produit",
      dataIndex: "product_name_snapshot",
      key: "product_name_snapshot",
    },
    {
      title: "Unité",
      key: "unit",
      render: (_, record) =>
        record.unit_short_name_snapshot || record.unit_name_snapshot || "-",
      width: 120,
    },
    {
      title: "Quantité",
      dataIndex: "quantity",
      key: "quantity",
      align: "right",
      render: (value) =>
        Number(value || 0).toLocaleString("fr-FR", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 5,
        }),
      width: 140,
    },
  ];

  return (
    <Modal
      title={
        delivery?.delivery_number
          ? `Bordereau ${delivery.delivery_number}`
          : "Détail du bordereau"
      }
      open={open}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>
          Fermer
        </Button>,
        <Button
          key="print"
          type="primary"
          icon={<FilePdfOutlined />}
          onClick={() => delivery && void onPrint(delivery)}
          disabled={!delivery || !canPrint}
        >
          Réimprimer le PDF
        </Button>,
      ]}
    >
      {!delivery ? (
        <Empty description="Aucun bordereau sélectionné." />
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="N° bordereau">
              {delivery.delivery_number}
            </Descriptions.Item>
            <Descriptions.Item label="Date livraison">
              {formatDate(delivery.delivery_date, true)}
            </Descriptions.Item>
            <Descriptions.Item label="Facture liée">
              {delivery.invoice_number || delivery.invoice_number_snapshot || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Client">
              {delivery.client_name || delivery.client_name_snapshot || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Établissement">
              {delivery.warehouse_name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Émis par">
              {delivery.delivered_by_name || delivery.delivered_by_name_snapshot || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Destinataire">
              {delivery.recipient_name}
            </Descriptions.Item>
            <Descriptions.Item label="Téléphone">
              {delivery.recipient_phone || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Statut livraison">
              <Tag color="green">Livrée</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Statut paiement">
              {delivery.payment_status || "-"}
            </Descriptions.Item>
          </Descriptions>

          <Card title="Articles livrés" size="small">
            <Table
              columns={itemColumns}
              dataSource={delivery.items || []}
              rowKey={(record) => String(record.id)}
              pagination={false}
              size="small"
            />
          </Card>

          <Card title="Remarques" size="small">
            <Text>{delivery.notes || "Aucune remarque."}</Text>
          </Card>
        </Space>
      )}
    </Modal>
  );
};

const GestionLivraisons: React.FC = () => {
  const { selectedCompany, selectedWarehouse } = useSelection();
  const { hasPermission } = useAuth();
  const [filterForm] = Form.useForm<DeliveryFiltersState>();
  const [deliveryForm] = Form.useForm();

  const selectedCompanyId = resolveContextId(selectedCompany);
  const selectedWarehouseId = resolveContextId(selectedWarehouse);

  const [activeTab, setActiveTab] = useState<"pending" | "history">("pending");
  const [eligibleSales, setEligibleSales] = useState<EligibleSaleForDelivery[]>([]);
  const [deliveryHistory, setDeliveryHistory] = useState<SalesDelivery[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSale, setSelectedSale] = useState<EligibleSaleForDelivery | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [deliveryDetail, setDeliveryDetail] = useState<SalesDeliveryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filters, setFilters] = useState<DeliveryFiltersState>({
    warehouse_id: selectedWarehouseId,
  });
  const [eligiblePagination, setEligiblePagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
  });

  const canCreateDelivery = hasPermission(
    "Gestion Commerciale.Ventes.Livraisons.create"
  );
  const canViewDelivery = hasPermission(
    "Gestion Commerciale.Ventes.Livraisons.view"
  );
  const canPrintDelivery = hasPermission(
    "Gestion Commerciale.Ventes.Livraisons.generate_pdf"
  );

  useEffect(() => {
    filterForm.setFieldsValue({
      warehouse_id: selectedWarehouseId,
    });
  }, [filterForm, selectedWarehouseId]);

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const companyId = selectedCompanyId;
        const warehouseId = selectedWarehouseId;

        const customerParams = new URLSearchParams();
        if (companyId) {
          customerParams.set("companyId", String(companyId));
        }
        if (warehouseId) {
          customerParams.set("warehouseId", String(warehouseId));
        }

        const warehousesParams = new URLSearchParams();
        if (companyId) {
          warehousesParams.set("company_id", String(companyId));
        }
        warehousesParams.set("limit", "200");

        const [customersResponse, warehousesResponse] = await Promise.all([
          fetch(
            `${API_CONFIG.API_URL}/users/customers${
              customerParams.toString() ? `?${customerParams.toString()}` : ""
            }`,
            { headers: getAuthHeaders() }
          ),
          fetch(
            `${API_CONFIG.API_URL}/warehouses${
              warehousesParams.toString() ? `?${warehousesParams.toString()}` : ""
            }`,
            { headers: getAuthHeaders() }
          ),
        ]);

        if (customersResponse.ok) {
          const customersData = await customersResponse.json();
          setClients(Array.isArray(customersData) ? customersData : []);
        }

        if (warehousesResponse.ok) {
          const warehousesData = await warehousesResponse.json();
          const entries = Array.isArray(warehousesData?.warehouses)
            ? warehousesData.warehouses
            : [];
          setWarehouses(entries);
        }
      } catch (error) {
        console.error("Erreur chargement référentiel livraisons:", error);
      }
    };

    void loadReferenceData();
  }, [selectedCompanyId, selectedWarehouseId]);

  const buildApiFilters = useMemo(() => {
    const [dateStart, dateEnd] = filters.date_range || [];
    return {
      company_id: selectedCompanyId,
      warehouse_id: filters.warehouse_id ?? selectedWarehouseId,
      user_id: filters.user_id,
      invoice_number: filters.invoice_number,
      delivery_number: filters.delivery_number,
      date_from: dateStart ? dateStart.format("YYYY-MM-DD") : undefined,
      date_to: dateEnd ? dateEnd.format("YYYY-MM-DD") : undefined,
    };
  }, [filters, selectedCompanyId, selectedWarehouseId]);

  const loadEligibleSales = async (page = eligiblePagination.page, limit = eligiblePagination.limit) => {
    setLoading(true);
    try {
      const response = await fetchEligibleSalesForDelivery({
        ...buildApiFilters,
        page,
        limit,
      });
      setEligibleSales(response.eligibleSales || []);
      setEligiblePagination({
        page: response.pagination?.page || page,
        limit: response.pagination?.limit || limit,
        total: response.pagination?.total || 0,
      });
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : "Erreur lors du chargement des ventes à livrer."
      );
    } finally {
      setLoading(false);
    }
  };

  const loadDeliveryHistory = async (
    page = historyPagination.page,
    limit = historyPagination.limit
  ) => {
    setHistoryLoading(true);
    try {
      const response = await fetchSalesDeliveries({
        ...buildApiFilters,
        status: "delivered",
        page,
        limit,
      });
      setDeliveryHistory(response.deliveries || []);
      setHistoryPagination({
        page: response.pagination?.page || page,
        limit: response.pagination?.limit || limit,
        total: response.pagination?.total || 0,
      });
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : "Erreur lors du chargement de l'historique des livraisons."
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (canViewDelivery) {
      void loadEligibleSales(1, eligiblePagination.limit);
      void loadDeliveryHistory(1, historyPagination.limit);
    }
  }, [buildApiFilters, canViewDelivery]);

  const openCreateModal = (sale: EligibleSaleForDelivery) => {
    setSelectedSale(sale);
    deliveryForm.setFieldsValue({
      delivery_date: dayjs(),
      recipient_name: sale.client_name || "",
      recipient_phone: "",
      notes: "",
    });
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setSelectedSale(null);
    deliveryForm.resetFields();
  };

  const handleCreateDelivery = async () => {
    if (!selectedSale) {
      return;
    }

    try {
      const values = await deliveryForm.validateFields();
      setSubmitting(true);
      const response = await createSalesDelivery({
        order_id: selectedSale.id,
        company_id: selectedCompanyId,
        delivery_date: values.delivery_date.toISOString(),
        recipient_name: values.recipient_name,
        recipient_phone: values.recipient_phone,
        notes: values.notes,
      });

      message.success(response.message || "Bordereau émis avec succès.");
      closeCreateModal();
      await Promise.all([
        loadEligibleSales(eligiblePagination.page, eligiblePagination.limit),
        loadDeliveryHistory(1, historyPagination.limit),
      ]);

      if (response.delivery && canPrintDelivery) {
        await downloadSalesDeliveryPdf(
          response.delivery.id,
          `Bordereau_${response.delivery.delivery_number}.pdf`
        );
      }
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrintDelivery = async (delivery: SalesDelivery) => {
    try {
      await downloadSalesDeliveryPdf(
        delivery.id,
        `Bordereau_${delivery.delivery_number}.pdf`
      );
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : "Erreur lors du téléchargement du bordereau."
      );
    }
  };

  const handleViewDelivery = async (delivery: SalesDelivery) => {
    try {
      setDetailLoading(true);
      setDeliveryDetail(null);
      setShowDetailModal(true);
      const detail = await fetchSalesDeliveryDetail(delivery.id, {
        company_id: selectedCompanyId,
      });
      setDeliveryDetail(detail);
    } catch (error) {
      setShowDetailModal(false);
      message.error(
        error instanceof Error
          ? error.message
          : "Erreur lors du chargement du bordereau."
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const eligibleColumns: ColumnsType<EligibleSaleForDelivery> = [
    {
      title: "Facture",
      dataIndex: "invoice_number",
      key: "invoice_number",
      width: 180,
    },
    {
      title: "Date vente",
      dataIndex: "order_date",
      key: "order_date",
      render: (value) => formatDate(value),
      width: 140,
    },
    {
      title: "Client",
      dataIndex: "client_name",
      key: "client_name",
    },
    {
      title: "Établissement",
      dataIndex: "warehouse_name",
      key: "warehouse_name",
      width: 180,
    },
    {
      title: "Paiement",
      dataIndex: "payment_status",
      key: "payment_status",
      width: 150,
      render: (value) => {
        let color = "default";
        if (value === "Payé") color = "green";
        if (value === "Partiellement payé") color = "orange";
        if (value === "Non payé") color = "red";
        return <Tag color={color}>{value || "-"}</Tag>;
      },
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      align: "right",
      render: (value) => formatCurrency(value),
      width: 140,
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      render: (_, record) => (
        <Button
          type="primary"
          icon={<FileAddOutlined />}
          onClick={() => openCreateModal(record)}
          disabled={!canCreateDelivery}
        >
          Émettre
        </Button>
      ),
    },
  ];

  const historyColumns: ColumnsType<SalesDelivery> = [
    {
      title: "Bordereau",
      dataIndex: "delivery_number",
      key: "delivery_number",
      width: 180,
    },
    {
      title: "Facture",
      dataIndex: "invoice_number",
      key: "invoice_number",
      width: 170,
    },
    {
      title: "Date livraison",
      dataIndex: "delivery_date",
      key: "delivery_date",
      render: (value) => formatDate(value, true),
      width: 170,
    },
    {
      title: "Client",
      dataIndex: "client_name",
      key: "client_name",
    },
    {
      title: "Établissement",
      dataIndex: "warehouse_name",
      key: "warehouse_name",
      width: 180,
    },
    {
      title: "Destinataire",
      dataIndex: "recipient_name",
      key: "recipient_name",
      width: 180,
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: () => <Tag color="green">Livrée</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 72,
      align: "center",
      render: (_, record) => (
        <RowActionsMenu
          items={[
            {
              key: "view",
              icon: <EyeOutlined />,
              label: "Voir le détail",
              onClick: () => void handleViewDelivery(record),
            },
            {
              key: "print",
              icon: <FilePdfOutlined />,
              label: canPrintDelivery
                ? "Télécharger le bordereau"
                : "Téléchargement indisponible",
              disabled: !canPrintDelivery,
              onClick: () => void handlePrintDelivery(record),
            },
          ]}
          ariaLabel={`Afficher les actions du bordereau ${
            record.delivery_number || record.id
          }`}
        />
      ),
    },
  ];

  if (!canViewDelivery) {
    return (
      <Card>
        <Empty description="Vous n'avez pas accès au module Livraisons." />
      </Card>
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card>
        <Space
          direction="vertical"
          size={12}
          style={{ width: "100%" }}
        >
          <Space align="center">
            <TruckOutlined style={{ fontSize: 20, color: "#1677ff" }} />
            <div>
              <Title level={4} style={{ margin: 0 }}>
                Bordereaux de livraison
              </Title>
              <Text type="secondary">
                Émission et réimpression des livraisons pour les ventes déjà réalisées.
              </Text>
            </div>
          </Space>

          <Form
            form={filterForm}
            layout="vertical"
            initialValues={{
              warehouse_id: selectedWarehouseId,
            }}
            onFinish={(values: DeliveryFiltersState) => {
              setFilters(values);
              setEligiblePagination((prev) => ({ ...prev, page: 1 }));
              setHistoryPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            <Row gutter={16}>
              <Col xs={24} md={6}>
                <Form.Item label="Établissement" name="warehouse_id">
                  <Select allowClear placeholder="Tous les établissements">
                    {warehouses.map((warehouse) => (
                      <Select.Option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Client" name="user_id">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    placeholder="Tous les clients"
                  >
                    {clients.map((client) => (
                      <Select.Option key={client.id} value={client.id}>
                        {getClientLabel(client)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={5}>
                <Form.Item label="N° facture" name="invoice_number">
                  <Input placeholder="Ex: DEF032026-0001" />
                </Form.Item>
              </Col>
              <Col xs={24} md={5}>
                <Form.Item label="N° bordereau" name="delivery_number">
                  <Input placeholder="Historique seulement" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Période" name="date_range">
                  <RangePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} md={16}>
                <Space style={{ marginTop: 30 }}>
                  <Button type="primary" htmlType="submit">
                    Filtrer
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      filterForm.resetFields();
                      const resetFilters = {
                        warehouse_id: selectedWarehouseId,
                      };
                      filterForm.setFieldsValue(resetFilters);
                      setFilters(resetFilters);
                    }}
                  >
                    Réinitialiser
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Space>
      </Card>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as "pending" | "history")}
          items={[
            {
              key: "pending",
              label: `À livrer (${eligiblePagination.total})`,
              children: (
                <Table
                  rowKey={(record) => String(record.id)}
                  columns={eligibleColumns}
                  dataSource={eligibleSales}
                  loading={loading}
                  pagination={{
                    current: eligiblePagination.page,
                    pageSize: eligiblePagination.limit,
                    total: eligiblePagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    onChange: (page, pageSize) =>
                      void loadEligibleSales(
                        pageSize === eligiblePagination.limit ? page : 1,
                        pageSize
                      ),
                  }}
                  locale={{
                    emptyText:
                      "Aucune vente éligible à la livraison pour les filtres courants.",
                  }}
                />
              ),
            },
            {
              key: "history",
              label: `Historique (${historyPagination.total})`,
              children: (
                <Table
                  rowKey={(record) => String(record.id)}
                  columns={historyColumns}
                  dataSource={deliveryHistory}
                  loading={historyLoading}
                  pagination={{
                    current: historyPagination.page,
                    pageSize: historyPagination.limit,
                    total: historyPagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    onChange: (page, pageSize) =>
                      void loadDeliveryHistory(
                        pageSize === historyPagination.limit ? page : 1,
                        pageSize
                      ),
                  }}
                  locale={{
                    emptyText:
                      "Aucun bordereau de livraison trouvé pour les filtres courants.",
                  }}
                />
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={
          selectedSale?.invoice_number
            ? `Émettre un bordereau pour ${selectedSale.invoice_number}`
            : "Émettre un bordereau"
        }
        open={showCreateModal}
        onCancel={closeCreateModal}
        onOk={() => void handleCreateDelivery()}
        confirmLoading={submitting}
        okText="Émettre et valider"
        destroyOnClose
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {selectedSale ? (
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Client">
                {selectedSale.client_name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Établissement">
                {selectedSale.warehouse_name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Montant">
                {formatCurrency(selectedSale.total)}
              </Descriptions.Item>
            </Descriptions>
          ) : null}

          <Form form={deliveryForm} layout="vertical">
            <Form.Item
              name="delivery_date"
              label="Date et heure de livraison"
              rules={[{ required: true, message: "Sélectionnez une date." }]}
            >
              <DatePicker showTime style={{ width: "100%" }} format="DD/MM/YYYY HH:mm" />
            </Form.Item>
            <Form.Item
              name="recipient_name"
              label="Nom du destinataire"
              rules={[{ required: true, message: "Saisissez le destinataire." }]}
            >
              <Input placeholder="Nom complet du destinataire" />
            </Form.Item>
            <Form.Item name="recipient_phone" label="Téléphone du destinataire">
              <Input placeholder="Numéro de téléphone" />
            </Form.Item>
            <Form.Item name="notes" label="Remarques">
              <Input.TextArea
                rows={3}
                placeholder="Observations de livraison"
              />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      <DeliveryDetailModal
        delivery={deliveryDetail}
        open={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setDeliveryDetail(null);
        }}
        onPrint={handlePrintDelivery}
        canPrint={canPrintDelivery}
      />

      {detailLoading && !deliveryDetail ? (
        <Modal open footer={null} closable={false}>
          <Empty description="Chargement du bordereau..." />
        </Modal>
      ) : null}
    </Space>
  );
};

export default GestionLivraisons;
